import {Controller} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["canvas", "topLeftX", "topLeftY", "bottomRightX", "bottomRightY"]

  connect() {
    this.ctx = this.canvasTarget.getContext('2d');

    const topLeftX = parseInt(this.topLeftXTarget.value);
    const topLeftY = parseInt(this.topLeftYTarget.value);
    const bottomRightX = parseInt(this.bottomRightXTarget.value);
    const bottomRightY = parseInt(this.bottomRightYTarget.value);

    this.rect = {
      x: topLeftX,
      y: topLeftY,
      w: bottomRightX - topLeftX,
      h: bottomRightY - topLeftY
    }

    this.handlesSize = 8;
    this.currentHandle = null;
    this.drag = false;
    this.isDraggingRectangle = false;

    this.canvasTarget.addEventListener('mousedown', this.mouseDown.bind(this), false);
    this.canvasTarget.addEventListener('mouseup', this.mouseUp.bind(this), false);
    this.canvasTarget.addEventListener('mousemove', this.mouseMove.bind(this), false);

    this.draw();
    this.updatePositionValues();
  }

  point(x, y) {
    return {x: x, y: y};
  }

  dist(p1, p2) {
    return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
  }

  getHandle(mouse) {
    const {x, y, w, h} = this.rect;
    if (this.dist(mouse, this.point(x, y)) <= this.handlesSize) return 'topleft';
    if (this.dist(mouse, this.point(x + w, y)) <= this.handlesSize) return 'topright';
    if (this.dist(mouse, this.point(x, y + h)) <= this.handlesSize) return 'bottomleft';
    if (this.dist(mouse, this.point(x + w, y + h)) <= this.handlesSize) return 'bottomright';
    return null;
  }

  isMouseInRectangle(mouse) {
    const {x, y, w, h} = this.rect;
    return mouse.x > x && mouse.x < x + w && mouse.y > y && mouse.y < y + h;
  }

  mouseDown(e) {
    const mousePos = this.point(e.offsetX, e.offsetY);
    this.currentHandle = this.getHandle(mousePos);

    if (this.currentHandle) {
      this.drag = true;
    } else if (this.isMouseInRectangle(mousePos)) {
      this.isDraggingRectangle = true;
      this.initialX = mousePos.x;
      this.initialY = mousePos.y;
    }

    this.draw();
  }

  mouseUp() {
    this.drag = false;
    this.isDraggingRectangle = false;
    this.currentHandle = null;
    this.updatePositionValues();  // Update the hidden input fields
    this.draw();
  }

  mouseMove(e) {
    const mousePos = this.point(e.offsetX, e.offsetY);

    if (!this.drag && !this.isDraggingRectangle) {
      this.currentHandle = this.getHandle(mousePos);
    }

    if (this.isDraggingRectangle) {
      const dx = mousePos.x - this.initialX;
      const dy = mousePos.y - this.initialY;

      this.rect.x = Math.max(0, Math.min(this.rect.x + dx, this.canvasTarget.width - this.rect.w));
      this.rect.y = Math.max(0, Math.min(this.rect.y + dy, this.canvasTarget.height - this.rect.h));

      this.initialX = mousePos.x;
      this.initialY = mousePos.y;

      this.draw();
    } else if (this.drag && this.currentHandle) {
      switch (this.currentHandle) {
        case 'topleft':
          const newX1 = Math.max(0, mousePos.x);
          const newY1 = Math.max(0, mousePos.y);
          this.rect.w += this.rect.x - newX1;
          this.rect.h += this.rect.y - newY1;
          this.rect.x = newX1;
          this.rect.y = newY1;
          break;
        case 'topright':
          const newY2 = Math.max(0, mousePos.y);
          this.rect.w = Math.min(mousePos.x, this.canvasTarget.width) - this.rect.x;
          this.rect.h += this.rect.y - newY2;
          this.rect.y = newY2;
          break;
        case 'bottomleft':
          const newX3 = Math.max(0, mousePos.x);
          this.rect.w += this.rect.x - newX3;
          this.rect.x = newX3;
          this.rect.h = Math.min(mousePos.y, this.canvasTarget.height) - this.rect.y;
          break;
        case 'bottomright':
          this.rect.w = Math.min(mousePos.x, this.canvasTarget.width) - this.rect.x;
          this.rect.h = Math.min(mousePos.y, this.canvasTarget.height) - this.rect.y;
          break;
      }
      this.draw();
    } else {
      this.draw();  // Redraw to update handle highlights
    }
  }

  draw() {
    const {x, y, w, h} = this.rect;
    this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height);
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    this.ctx.fillRect(x, y, w, h);

    // Draw handles
    this.drawHandle(x, y, 'topleft');
    this.drawHandle(x + w, y, 'topright');
    this.drawHandle(x, y + h, 'bottomleft');
    this.drawHandle(x + w, y + h, 'bottomright');
  }

  drawHandle(x, y, handle) {
    if (this.currentHandle === handle) {
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Match the rectangle color
      this.ctx.beginPath();
      this.ctx.arc(x, y, this.handlesSize, 0, 2 * Math.PI);
      this.ctx.fill();
    }
  }

  updatePositionValues() {
    this.topLeftXTarget.value = this.rect.x;
    this.topLeftYTarget.value = this.rect.y;
    this.bottomRightXTarget.value = this.rect.x + this.rect.w;
    this.bottomRightYTarget.value = this.rect.y + this.rect.h;
  }
}
